import { LoadingButton } from '@mui/lab'
import {
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  TextField,
} from '@mui/material'
import Grid from '@mui/material/Grid2'
import { Base64 } from 'js-base64'
import { useSnackbar } from 'notistack'
import React, { useContext, useState } from 'react'

import { AppContext } from 'providers/context/App'
import { getBoulevardAuthToken, loginTokenCreateApi } from 'services/Session'
import {
  getUserAuthSessionApi,
  sendSubscribeSMS,
  signUpCreateUserApi,
  updatePhoneNumber,
} from 'services/User'
import {
  APPOINTMENT_CONFIRMATION_MESSAGE,
  BOULEVARD_AUTH_API_TOKEN,
  CUSTOMER_ACCESS_TOKEN,
  CUSTOMER_SESSION_TOKEN,
  IS_BOULEVARD_CLIENT,
} from 'utils/constants/Helpers'
import { LOGIN_MESSAGES, SIGNUP_MESSAGES } from 'utils/constants/Messages'
import {
  numberOnly,
  setLocalstorageItem,
  textTrimmer,
} from 'utils/helper-functions'
import {
  LoginPayloadVariables,
  LoginSignupFieldValidate,
  LoginSignupState,
} from 'utils/types/sessionTypes'
import { ClientInfoResponse } from 'utils/types/userTypes'
import validator, { signupSchema } from 'utils/yupSchema'
import MarketingText from '../marketing-text'

import { ReactComponent as EyeSlash } from 'assets/icons/eye-slash.svg'
import { ReactComponent as Eye } from 'assets/icons/eye.svg'

interface Props {
  email: string
  setEmailError: (data: string) => void
  isMembership?: boolean
}

const AppointmentSignUp = ({ email, setEmailError, isMembership }: Props) => {
  const { appMasterData, updateData } = useContext(AppContext)
  const { enqueueSnackbar } = useSnackbar()

  const [loader, setLoader] = useState(false)
  const [values, setValues] = useState<LoginSignupState>({
    password: '',
    emailId: email,
    firstName: '',
    lastName: '',
    phoneNumber: '',
    showPassword: false,
    mobileNumber: '',
    isSMSOptInChecked: false,
  })
  const [signupStatus, setSignupStatus] = useState(true)
  const [errors, setErrors] = useState<LoginSignupFieldValidate>({
    emailId: '',
    firstName: '',
    lastName: '',
    password: '',
    phoneNumber: '',
    mobileNumber: '',
  })

  const SMSOptInLabel = isMembership
    ? 'Text me member reminders, promos & new services.'
    : 'Text me the good stuff – promos & new services.'
  let base64AccessToken = ''

  const handleChange =
    (prop: keyof LoginSignupState) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (prop === 'phoneNumber' && event.target.value) {
        const fieldvalid = { ...errors }
        fieldvalid.phoneNumber = ''
        if (!numberOnly(textTrimmer(event.target.value))) {
          fieldvalid.phoneNumber = SIGNUP_MESSAGES.PHONE_NUMBER_INVALID
        }
        setErrors(fieldvalid)
      }

      if (prop === 'mobileNumber' && event.target.value) {
        const fieldvalid = { ...errors }
        fieldvalid.mobileNumber = ''
        if (!numberOnly(textTrimmer(event.target.value))) {
          fieldvalid.mobileNumber = SIGNUP_MESSAGES.MOBILE_NUMBER_INVALID
        }
        setErrors(fieldvalid)
      }

      const newValues = {
        ...values,
        [prop]:
          prop === 'isSMSOptInChecked'
            ? event.target.checked
            : event.target.value,
      }

      if (prop === 'isSMSOptInChecked' && newValues.isSMSOptInChecked) {
        newValues.mobileNumber = values.phoneNumber
      }

      const checkSMSOptCondition =
        (newValues.isSMSOptInChecked && newValues.mobileNumber) ||
        !newValues.isSMSOptInChecked

      if (
        email &&
        newValues.firstName &&
        newValues.lastName &&
        newValues.password &&
        newValues.phoneNumber &&
        checkSMSOptCondition
      ) {
        setSignupStatus(false)
      } else {
        setSignupStatus(true)
      }

      setValues(newValues)
    }

  const handleClickShowPassword = () => {
    setValues({
      ...values,
      showPassword: !values.showPassword,
    })
  }

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault()
  }

  const phoneNumberUpdate = async () => {
    let payload = {
      first_name: values.firstName,
      last_name: values.lastName,
      phone: values.phoneNumber,
    }
    const res: ClientInfoResponse = await updatePhoneNumber(
      base64AccessToken,
      payload
    )
    if (res) {
      updateData({ userInfo: res })
    }
  }

  const registerUser = async (e: React.SyntheticEvent) => {
    try {
      e.preventDefault()
      const { success, errors: yupErrors } =
        await validator<LoginSignupFieldValidate>(signupSchema, {
          ...values,
          emailId: email || '',
        })
      setErrors(yupErrors)
      if (yupErrors.emailId) setEmailError(yupErrors.emailId)
      else setEmailError('')
      setLoader(true)
      if (success) {
        const {
          password = '',
          emailId = '',
          firstName = '',
          lastName = '',
        } = values
        const variables = {
          password,
          email: emailId,
          firstName,
          lastName,
        }
        setLoader(true)
        const res = await signUpCreateUserApi(variables)
        if (res?.customerCreate?.customer?.id) {
          const userObj = await userLogin({ email: emailId, password }, true)
          updateData(userObj)
          await phoneNumberUpdate()
          if (values.isSMSOptInChecked && values.mobileNumber) {
            await sendSubscribeSMS(values)
          }
        } else {
          throw Error(SIGNUP_MESSAGES.SIGNUP_FAILED)
        }
      }
    } catch (error) {
      enqueueSnackbar(SIGNUP_MESSAGES.SIGNUP_FAILED, {
        variant: 'error',
      })
    } finally {
      setLoader(false)
    }
  }

  const userLogin = async (
    payload: LoginPayloadVariables,
    preventUpdate: boolean = false
  ) => {
    let result = { user: { token: '' } }
    const res = await loginTokenCreateApi(payload)
    if (res?.customerAccessTokenCreate?.customerAccessToken?.accessToken) {
      const accessToken =
        res.customerAccessTokenCreate.customerAccessToken.accessToken
      setLocalstorageItem(CUSTOMER_ACCESS_TOKEN, accessToken)
      base64AccessToken = Base64.encode(
        res.customerAccessTokenCreate.customerAccessToken.accessToken
      )

      const sessionRes = await getUserAuthSessionApi(base64AccessToken, payload)
      if (sessionRes?.token) {
        setLocalstorageItem(CUSTOMER_SESSION_TOKEN, sessionRes?.token)
        result = { user: { token: sessionRes?.token } }
        if (!preventUpdate) {
          updateData(result)
        }
        const authTokenRes = await getBoulevardAuthToken()
        if (authTokenRes?.is_boulevard_client) {
          setLocalstorageItem(BOULEVARD_AUTH_API_TOKEN, authTokenRes?.token)
        }
        setLocalstorageItem(
          IS_BOULEVARD_CLIENT,
          authTokenRes?.is_boulevard_client
            ? authTokenRes?.is_boulevard_client.toString()
            : 'false'
        )

        enqueueSnackbar(SIGNUP_MESSAGES.SIGNUP_SUCCESS, {
          variant: 'warning',
        })
      } else {
        enqueueSnackbar(LOGIN_MESSAGES.ERROR, {
          variant: 'error',
        })
      }
    } else {
      enqueueSnackbar(LOGIN_MESSAGES.ERROR, {
        variant: 'error',
      })
    }
    return result
  }

  return (
    <form
      onSubmit={(e) => {
        registerUser(e).catch((e) => console.error(e))
      }}
    >
      {!appMasterData.isLoggedIn && (
        <>
          <Grid className="input-filed-wrapper">
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 6,
                }}
              >
                <TextField
                  id="first-name"
                  className="txt-field"
                  label="First Name"
                  variant="filled"
                  fullWidth
                  onChange={handleChange('firstName')}
                  value={values.firstName}
                  helperText={errors.firstName || ''}
                  error={Boolean(errors.firstName)}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                }}
              >
                <TextField
                  id="last-name"
                  className="txt-field"
                  label="Last Name"
                  variant="filled"
                  fullWidth
                  onChange={handleChange('lastName')}
                  value={values.lastName}
                  helperText={errors.lastName || ''}
                  error={Boolean(errors.lastName)}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid className="input-filed-wrapper">
            <FormControl fullWidth variant="filled">
              <InputLabel htmlFor="standard-adornment-phone">
                Phone Number
              </InputLabel>
              <OutlinedInput
                className="txt-field"
                id="standard-adornment-phone"
                type="text"
                value={values.phoneNumber}
                error={Boolean(errors.phoneNumber)}
                onChange={handleChange('phoneNumber')}
                inputProps={{
                  maxLength: 15,
                }}
              />
              <FormHelperText error={Boolean(errors.phoneNumber)}>
                {errors.phoneNumber || APPOINTMENT_CONFIRMATION_MESSAGE}
              </FormHelperText>
            </FormControl>
            <FormControlLabel
              className="sms-opt-in-checkbox"
              control={
                <Checkbox
                  checked={values.isSMSOptInChecked}
                  onChange={handleChange('isSMSOptInChecked')}
                />
              }
              label={SMSOptInLabel}
            />
          </Grid>
          {values.isSMSOptInChecked && (
            <Grid className="input-filed-wrapper">
              <FormControl fullWidth variant="filled">
                <InputLabel htmlFor="standard-adornment-mobile">
                  Mobile Phone Number
                </InputLabel>
                <OutlinedInput
                  className="txt-field"
                  id="standard-adornment-mobile"
                  type="text"
                  value={values.mobileNumber}
                  error={Boolean(errors.mobileNumber)}
                  onChange={handleChange('mobileNumber')}
                  inputProps={{
                    maxLength: 15,
                  }}
                />
                {errors.mobileNumber && (
                  <FormHelperText error={Boolean(errors.mobileNumber)}>
                    {errors.mobileNumber}
                  </FormHelperText>
                )}
                <MarketingText />
              </FormControl>
            </Grid>
          )}
          <Grid className="input-filed-wrapper">
            <FormControl fullWidth variant="filled" className="text-box">
              <InputLabel htmlFor="standard-adornment-password">
                Password
              </InputLabel>
              <OutlinedInput
                className="txt-field"
                id="standard-adornment-password"
                type={values.showPassword ? 'text' : 'password'}
                value={values.password}
                error={Boolean(errors.password)}
                onChange={handleChange('password')}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {!values.showPassword ? <EyeSlash /> : <Eye />}
                    </IconButton>
                  </InputAdornment>
                }
              />
              <FormHelperText error={Boolean(errors.password)}>
                {errors.password || 'Must be 5 characters long'}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid className="input-filed-wrapper-btn">
            <LoadingButton
              variant="contained"
              className={signupStatus ? 'signup-btn' : 'app-btn'}
              fullWidth
              type="submit"
              onClick={(e) => {
                registerUser(e).catch((e) => console.error(e))
              }}
              disabled={signupStatus}
              loading={loader}
              loadingIndicator={
                <CircularProgress color={'primary'} size={20} />
              }
            >
              Sign Up
            </LoadingButton>
          </Grid>
        </>
      )}
    </form>
  )
}

export default AppointmentSignUp
